import { useCallback, useMemo } from "react";
import {
  useQuery as apolloUseQuery,
  useLazyQuery as apolloUseLazyQuery,
  useMutation as apolloUseMutation
} from "@apollo/client/react";
import { objNamesToCamelCase } from "common/utils/helpers/cases";
import { transformNullToUndefined } from "./transformNullToUndefined";

const EXCLUDED_KEYS = ["__typename", "__ref"];

const transformGraphqlData = <T>(data: T): T =>
  data ? objNamesToCamelCase(transformNullToUndefined(data), EXCLUDED_KEYS) : data;

/**
 * Custom useQuery hook that transforms null values to undefined
 * This provides TypeScript-friendly undefined values while keeping Apollo Client cache intact
 */
export const useQuery = ((...args: Parameters<typeof apolloUseQuery>) => {
  const result = apolloUseQuery(...args);

  const transformedData = useMemo(() => transformGraphqlData(result.data), [result.data]);

  return {
    ...result,
    data: transformedData
  };
}) as typeof apolloUseQuery;

/**
 * Custom useLazyQuery hook that transforms null values to undefined
 */
export const useLazyQuery = ((...args: Parameters<typeof apolloUseLazyQuery>) => {
  const [executeQuery, result] = apolloUseLazyQuery(...args);

  const transformedData = useMemo(() => transformGraphqlData(result.data), [result.data]);

  const wrappedExecuteQuery = useCallback(
    async (...queryArgs: Parameters<typeof executeQuery>) => {
      const queryResult = await executeQuery(...queryArgs);
      return { ...queryResult, data: transformGraphqlData(queryResult.data) };
    },
    [executeQuery]
  );

  const transformedResult = {
    ...result,
    data: transformedData
  };

  return [wrappedExecuteQuery, transformedResult];
}) as typeof apolloUseLazyQuery;

/**
 * Custom useMutation hook that transforms null values to undefined in the result data
 */
export const useMutation = ((...args: Parameters<typeof apolloUseMutation>) => {
  const [mutate, result] = apolloUseMutation(...args);

  const transformedData = useMemo(() => transformGraphqlData(result.data), [result.data]);

  const wrappedMutate = useCallback(
    async (...mutateArgs: Parameters<typeof mutate>) => {
      const mutateResult = await mutate(...mutateArgs);
      return { ...mutateResult, data: transformGraphqlData(mutateResult.data) };
    },
    [mutate]
  );

  const transformedResult = {
    ...result,
    data: transformedData
  };

  return [wrappedMutate, transformedResult];
}) as typeof apolloUseMutation;
